import { PINATA_GATEWAY_URL, appConfig } from "@/constants";
import type { CryptoFiatPrices, FiatEquivalentPrice } from "@/context";
import {
  type Bid,
  EnvironmentNames,
  type Erc20Token,
  type PricingData,
} from "@/types";
import type * as orderbook from "@imtbl/orderbook";
import type { CollectionBid } from "@imtbl/orderbook";
import type * as passport from "@imtbl/passport";

import { BigNumber } from "bignumber.js";

export function toSimpleAddressFormat(address: string): string {
  return `${address.slice(0, 6)}...${address.slice(-4)}`.toLowerCase();
}

export const isMatchingAddress = (addressA = "", addressB = "") =>
  addressA.toLowerCase() === addressB.toLowerCase();

/**
 * Checks if a token is available in a given allow list
 * @param contractAddress - The token contract address to check
 * @param allowList - The list of allowed tokens
 * @returns boolean indicating if the token is available
 */
export const checkTokenAvailability = <T extends { address?: string }>(
  contractAddress: string,
  allowList: T[],
): boolean =>
  allowList.find(
    (tokenInfo) =>
      tokenInfo.address &&
      isMatchingAddress(tokenInfo.address, contractAddress),
  ) !== undefined;

export const safeTestId = (testId: string): string =>
  `${testId}`.replace(/[^a-zA-Z0-9-]/g, "-");

export const getMediaUrl = (url: string | undefined) => {
  if (url?.startsWith("ipfs://")) {
    const cid = url.split("ipfs://")[1];
    return `${PINATA_GATEWAY_URL}/ipfs/${cid}`;
  }
  return url; // If it's not an IPFS URL, return as is
};

export const convertTokenAmount = (
  amount: string,
  decimals: number,
): { value: string; isApproximate: boolean } => {
  const value = new BigNumber(amount).div(10 ** decimals);
  const decimalPlaces = value.decimalPlaces();

  if (decimalPlaces === null) {
    return { value: "", isApproximate: false };
  }
  if (value.isGreaterThanOrEqualTo(1)) {
    // From 1 and above, show 2 decimal places
    // If there are more than 2 decimal places, set isApproximate to true
    return {
      value: value.toFormat(2),
      isApproximate: decimalPlaces >= 2,
    };
  }

  // Value is between 0 and 1
  if (decimalPlaces <= 5) {
    // 0 to 5 decimal places, no tilda
    return { value: value.toFormat(decimalPlaces), isApproximate: false };
  }
  // More than 5 decimal places, round to 5 and set tilda to true
  return {
    value: value.toFormat(5, BigNumber.ROUND_UP),
    isApproximate: true,
  };
};

export const getZkEvmExplorerUrl = () =>
  appConfig.ENVIRONMENT === EnvironmentNames.PRODUCTION
    ? "https://explorer.immutable.com"
    : "https://explorer.testnet.immutable.com";

export const flattenToString = (
  value: string | string[] | undefined,
): string => {
  if (value) {
    if (Array.isArray(value)) {
      return value.join(" ");
    }
    return value;
  }
  return "";
};

export const isSandbox = () =>
  appConfig.ENVIRONMENT === EnvironmentNames.SANDBOX;

export const isImmutableUser = (userInfo: passport.UserProfile) => {
  return userInfo.email && userInfo.email.endsWith("@immutable.com") === true;
};

export function timeAgo(dateString?: string): string {
  if (!dateString) {
    return "";
  }
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime(); // Difference in milliseconds

  // Convert milliseconds to days
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays < 30) {
    if (diffInDays === 0) return "Last sold today";
    if (diffInDays === 1) return "Last sold 1 day ago";
    return `Last sold ${diffInDays} days ago`;
  }

  // Convert days to months (approximate)
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    if (diffInMonths === 1) return "Last sold 1 month ago";
    return `Last sold ${diffInMonths} months ago`;
  }

  // Convert months to years
  const diffInYears = Math.floor(diffInMonths / 12);
  if (diffInYears === 1) return "Last sold 1 year ago";
  return `Last sold ${diffInYears} years ago`;
}

export function getPrice(
  pricingData: PricingData | undefined,
  tokens: Map<string, Erc20Token>,
  getFormattedPrice: (
    symbol: string | undefined,
    decimals: number | undefined,
    amount: string,
  ) => string,
): string | undefined {
  if (!pricingData) return undefined;
  const token =
    pricingData.token.type === "NATIVE"
      ? { decimals: 18, symbol: "IMX" }
      : tokens.get(pricingData.token.contract_address);
  if (token === undefined) {
    return pricingData.amount;
  }
  return getFormattedPrice(token.symbol, token.decimals, pricingData.amount);
}

export const getFormattedNetPrice = (
  bid: orderbook.CollectionBid | Bid,
  tokens: Map<string, Erc20Token>,
): string => {
  const isCollectionBid = "sell" in bid;
  // Determine the token address and total amount based on bid type (i.e. CollectionBid or ItemBid)
  const tokenAddress = isCollectionBid
    ? bid.sell[0].contractAddress
    : bid.token_address;

  const totalAmount = BigInt(isCollectionBid ? bid.sell[0].amount : bid.amount);

  const originalQuantity = isCollectionBid
    ? BigInt(bid.buy[0].amount)
    : BigInt(bid.amount) / BigInt(bid.unit_price);

  // Calculate total fees for types PROTOCOL and ROYALTY
  const totalFees = (bid.fees || [])
    .filter((fee) => fee.type === "PROTOCOL" || fee.type === "ROYALTY")
    .reduce((acc, fee) => acc + BigInt(fee.amount), BigInt(0));

  const netAmount = totalAmount - totalFees;
  const netUnitAmount = netAmount / originalQuantity;

  const token = tokens.get(tokenAddress);
  const tokenAmount = convertTokenAmount(
    netUnitAmount.toString(),
    token?.decimals ?? 18,
  );

  return token
    ? `${token.symbol} ${tokenAmount.value}`
    : netUnitAmount.toString();
};

// Utility function to throttle function calls
export const throttle = <T extends (...args: unknown[]) => unknown>(
  func: T,
  delay: number,
): ((...args: Parameters<T>) => void) => {
  let lastCall = 0;
  let throttled = false;

  return (...args: Parameters<T>) => {
    const now = Date.now();

    // If we're not currently throttled, execute immediately
    if (!throttled) {
      func(...args);
      lastCall = now;
      throttled = true;

      // Set a timeout to allow the next execution after delay
      setTimeout(() => {
        throttled = false;
      }, delay);
    }
  };
};

export function sortBidsByUsd(
  bids: CollectionBid[],
  tokens: Map<string, Erc20Token>,
  fiatPrices: CryptoFiatPrices | undefined,
  buildFiatEquivalentPrice: (
    tokenPrice: string,
    decimals: number,
    tokenSymbol: string,
    fiatPrices: CryptoFiatPrices | undefined,
    fiatCurrency?: string,
  ) => FiatEquivalentPrice | undefined,
) {
  const clonedBids = [...bids];

  return clonedBids
    .map((bid) => {
      const contractAddress = bid.sell[0]?.contractAddress?.toLowerCase() || "";
      const token = tokens.get(contractAddress);
      if (!token) {
        return { bid, usdValue: new BigNumber(0) };
      }

      const sellAmount = BigInt(bid.sell[0]?.amount ?? "0");
      const buyAmount = BigInt(bid.buy[0]?.amount ?? "1");

      const numericPrice = buyAmount > 0n ? sellAmount / buyAmount : sellAmount;

      let usdValue = new BigNumber(0);
      if (fiatPrices) {
        const fiatEquivalent = buildFiatEquivalentPrice(
          numericPrice.toString(),
          token.decimals ?? 18,
          token.symbol ?? "",
          fiatPrices,
          "usd",
        );
        if (fiatEquivalent) {
          // convert the fiat equivalent to a BigNumber
          usdValue = fiatEquivalent.amountBN;
        }
      }
      return { bid, usdValue };
    })
    .sort((a, b) => b.usdValue.minus(a.usdValue).toNumber());
}

export const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));
